import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import {
  Calendar,
  CheckCircle,
  MultiplePages,
  Page,
  Plus,
  ProfileCircle,
  Search as SearchIcon,
  TaskList,
} from 'iconoir-react'
import { useEffect, useState } from 'react'
import { SegmentedControl } from 'app/components/action-center/SegmentedControl'
import { TOOLBAR_CONTAINER } from 'app/components/CompactLabel'
import { ContactDrawer } from 'app/components/contacts/ContactDrawer'
import { ControlledSearch } from 'app/components/ui/controlled-search'
import { PageHeader } from 'app/components/PageHeader'
import { Button } from 'app/components/ui/button'
import { TableFiltersView as TableFilters } from 'app/components/ui/table-filters/table-filters'
import { useHasActiveFilters } from 'app/components/ui/table-filters/use-has-active-filters'
import { TableEmptyState } from 'app/components/ui/table-empty-state'
import { TasksCalendar } from 'app/components/tasks/TasksCalendar'
import { TasksTable, type GroupBy } from 'app/components/tasks/TasksTable'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from 'app/components/ui/dropdown-menu'
import { FilterObject, FilterValueType, decodeFilters, encodeFilters } from 'app/filter.utils'
import { buildClearedFilterParams } from 'app/lib/clearFilterParams'
import type { UseTasksReturn } from './useTasks'
import { useIsMobile } from 'app/hooks/useMediaQuery'
import { useHandleSearchParams } from 'app/utils'

type ViewMode = `calendar` | `table`

const GROUP_BY_OPTIONS: { id: GroupBy; label: string }[] = [
  { id: `due`, label: `Due date` },
  { id: `status`, label: `Status` },
  { id: `owner`, label: `Assignee` },
  { id: `none`, label: `No grouping` },
]

export type TasksComponentProps = UseTasksReturn

export const TasksComponent = observer(function TasksComponent(props: TasksComponentProps) {
  const {
    me,
    data,
    searchParams,
    setSearchParams,
    openContactDrawer,
    focusedContact,
    groups,
    filterableColumns,
    totalItems,
    refreshTrigger,
    highlightTaskId,
    drawerTaskTraversal,
    onTaskTraversalItemsChangeByGroup,
    onContactClickByGroup,
    handleTaskCreatedOrUpdated,
    handleSearchChange,
    onTotalChange,
    handleContactDrawerClose,
    handleContactChange,
    openAddNewTaskModal,
  } = props
  const isMobile = useIsMobile()
  const handleSearchParams = useHandleSearchParams()
  // Reads the live `filters` URL param. The tab defaults (status:open on Open
  // Tasks, ownerId on My Tasks) are synthesized in the route and never written
  // to the URL, so they correctly do not count as the user narrowing anything.
  const hasUrlFilters = useHasActiveFilters(filterableColumns, data.initialFilters)

  if (!me) return null

  // Flat-mode: useTasks always produces a single "All" group. Pull it directly so
  // we render exactly one view component without a per-group iteration.
  const allGroup = groups?.[0]

  // View mode lives in the URL so it bookmarks and survives refresh, but
  // we also stash the last choice in localStorage so navigating away from
  // /tasks and back remembers the preference even when the URL was reset.
  // Resolution order on each render:
  //   1. URL `?view=` (explicit, bookmarkable)
  //   2. localStorage value
  //   3. fallback to `table` (the canonical default: stakeholders want
  //      the spreadsheet-style flat list first; the calendar is opt-in)
  // A retired `inbox` value from either source falls through to `table`.
  const TASKS_VIEW_STORAGE_KEY = `tasks:view`
  const viewParam = searchParams.get(`view`)
  const storedView =
    typeof window !== `undefined` ? (window.localStorage.getItem(TASKS_VIEW_STORAGE_KEY) as ViewMode | null) : null
  const view: ViewMode =
    viewParam === `table` || viewParam === `calendar`
      ? viewParam
      : storedView === `table` || storedView === `calendar`
        ? storedView
        : `table`
  const tab = searchParams.get(`tab`)
  const tabDefaultGroupBy: GroupBy = tab === `all-tasks` ? `none` : `due`
  const [groupBy, setGroupBy] = useState<GroupBy>(tabDefaultGroupBy)
  useEffect(() => {
    setGroupBy(tabDefaultGroupBy)
  }, [tab])
  const groupByLabel = GROUP_BY_OPTIONS.find((option) => option.id === groupBy)?.label
  const searchValue = searchParams.get(`search`) ?? ``
  const hasNarrowed = !!searchValue || hasUrlFilters
  const clearSearchAndFilters = () => {
    handleSearchParams({
      search: undefined,
      ...buildClearedFilterParams({ columns: filterableColumns, searchParams }),
    })
  }
  // Slug-keyed like the Contacts page's empty states.
  const tasksEmptyState = hasNarrowed ? (
    <TableEmptyState
      icon={<SearchIcon className="h-6 w-6" strokeWidth={1.5} />}
      title="No matches"
      description={searchValue ? `No tasks match "${searchValue}".` : `No tasks match the current filters.`}
      action={
        <Button variant="secondary" size="sm" onClick={clearSearchAndFilters}>
          Clear search and filters
        </Button>
      }
    />
  ) : data.slug === `open-tasks` ? (
    <TableEmptyState
      tone="positive"
      icon={<CheckCircle className="h-6 w-6" strokeWidth={1.5} />}
      title="You are all caught up"
      description="Every task has been closed out. New follow ups land here the moment a call, form, or workflow creates one."
    />
  ) : data.slug === `my-tasks` ? (
    <TableEmptyState
      tone="positive"
      icon={<CheckCircle className="h-6 w-6" strokeWidth={1.5} />}
      title="Nothing assigned to you"
      description="You have no open or completed tasks of your own. Anything your team assigns to you shows up here."
    />
  ) : data.slug === `all-tasks` ? (
    <TableEmptyState
      icon={<TaskList className="h-6 w-6" strokeWidth={1.5} />}
      title="No tasks yet"
      description="Tasks are the follow ups your team owes a patient. Calls, forms, and workflows create them automatically, and you can add one yourself at any time."
      action={
        <Button variant="primary" size="sm" onClick={openAddNewTaskModal}>
          New Task
        </Button>
      }
    />
  ) : (
    // Custom pages carry filters of their own, so an empty one says nothing
    // about whether the practice has tasks. "All Tasks" is the only tab where
    // empty really does mean there are none.
    <TableEmptyState
      icon={<TaskList className="h-6 w-6" strokeWidth={1.5} />}
      title="Nothing in this list"
      description="Tasks show up here on their own as soon as they match this page. None of them do right now."
    />
  )
  const setView = (next: ViewMode) => {
    // Persist so the choice survives leaving the page.
    if (typeof window !== `undefined`) window.localStorage.setItem(TASKS_VIEW_STORAGE_KEY, next)
    setSearchParams((prev) => {
      // Keep the URL canonical: table is the default, so we omit `view=table`.
      // The calendar's own position (`cal`, `day`) means nothing to the table.
      if (next === `table`) {
        prev.delete(`view`)
        prev.delete(`cal`)
        prev.delete(`day`)
      } else prev.set(`view`, next)
      return prev
    })
  }
  // If the URL didn't specify a view but localStorage has a non-default
  // preference, surface it in the URL on initial load so the rest of the
  // app (and any bookmarked link) sees the actual rendered state.
  useEffect(() => {
    if (viewParam) return
    if (storedView === `calendar`) {
      setSearchParams((prev) => {
        prev.set(`view`, `calendar`)
        return prev
      })
    }
  }, [])

  // oxlint-disable-next-line react/no-unstable-nested-components -- render helper intentionally captures parent state
  function AssignedToMeFilterToggle() {
    const [isActive, setIsActive] = useState(false)

    const toggleActive = () => {
      if (searchParams.get(`tab`) === `my-tasks`) return
      setIsActive((current) => !current)
      // Decode from `data.urlFilters` (the *resolved* filter set including
      // tab defaults) rather than `searchParams.get('filters')` (which is
      // empty when only tab defaults are active). Otherwise toggling
      // "Assigned to me" on Open Tasks would drop the tab's status=open
      // default and you'd suddenly see closed tasks too.
      let currentFilters: FilterObject[] = decodeFilters(data.urlFilters)
      if (isActive) {
        const currentFilter = currentFilters.find((f) => f.attribute === `ownerId`)!
        if (Array.isArray(currentFilter.value)) {
          if (currentFilter.value.length === 1) {
            currentFilters = currentFilters.filter((f) => f.attribute !== `ownerId`)
          } else {
            currentFilter.value = currentFilter.value.filter((id) => id !== me!.id)
          }
        }
      } else {
        const existing = currentFilters.find((f) => f.attribute === `ownerId`)
        if (existing) {
          existing.value?.concat(me!.id)
        } else {
          currentFilters.push({
            attribute: `ownerId`,
            value: [me!.id],
            operator: `in`,
            displayName: `Owner`,
            type: FilterValueType.id,
          })
        }
      }
      setSearchParams((prev) => {
        prev.set(`filters`, encodeFilters(currentFilters))
        return prev
      })
    }

    useEffect(() => {
      // My Tasks scopes itself with a base search (me or my role), not an ownerId filter.
      if (searchParams.get(`tab`) === `my-tasks`) {
        setIsActive(true)
        return
      }
      const currentFilters: FilterObject[] = decodeFilters(searchParams.get(`filters`))
      const currentFilter = currentFilters.find((f) => f.attribute === `ownerId`) || data.preAppliedFilters.ownerId
      if (currentFilter) {
        const urlValues = Array.isArray(currentFilter.value) ? currentFilter.value : [currentFilter.value]
        setIsActive(urlValues.includes(me!.id))
      } else {
        setIsActive(false)
      }
    }, [searchParams])

    const lockedToMyTasks = searchParams.get(`tab`) === `my-tasks`
    return (
      <Button
        type="button"
        variant="white"
        size="md"
        onClick={toggleActive}
        disabled={lockedToMyTasks}
        className={classNames(
          `gap-x-2`,
          isActive ? `text-blue-600 hover:text-blue-600` : `text-default`,
          lockedToMyTasks && `cursor-not-allowed opacity-100`,
        )}
        aria-pressed={isActive}
        title={lockedToMyTasks ? `Tasks assigned to you or your role` : undefined}
      >
        <ProfileCircle
          className={lockedToMyTasks ? `text-minimal` : isActive ? `text-blue-600` : `text-default`}
          width={16}
          height={16}
        />
        <span className="hidden text-ui-xs md:inline">Assigned to me</span>
      </Button>
    )
  }

  // Open Tasks and All Tasks are one page with completed tasks hidden or shown. The other
  // tabs and custom lists carry a status scope of their own, so the toggle stays off them.
  const canShowCompleted = data.slug === `open-tasks` || data.slug === `all-tasks`
  const showCompleted = data.slug === `all-tasks`
  const toggleShowCompleted = () =>
    setSearchParams((prev) => {
      prev.set(`tab`, showCompleted ? `open-tasks` : `all-tasks`)
      // Once the user has edited filters the tab's status default lives in the URL with them.
      const userFilters = prev.get(`filters`)
      if (userFilters) {
        const rest = decodeFilters(userFilters).filter((filter) => filter.attribute !== `status.keyword`)
        const openStatus: FilterObject = {
          attribute: `status.keyword`,
          value: `open`,
          operator: `exactly`,
          displayName: `Status`,
          type: FilterValueType.string,
        }
        prev.set(`filters`, encodeFilters(showCompleted ? [...rest, openStatus] : rest))
      }
      return prev
    })

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        className="px-10 pt-8"
        title={data.activeSubpage}
        primaryButtonSlot={
          <button
            className={isMobile ? `btn btn-tertiary btn-sm` : `btn btn-primary btn-md`}
            onClick={openAddNewTaskModal}
            aria-label="New Task"
          >
            <Plus className="h-5 w-5" />
            {!isMobile && <span className="ml-1.5">New Task</span>}
          </button>
        }
        totalItems={isMobile ? undefined : totalItems}
      />
      {/* Two-group toolbar with consistent `gap-2` everywhere. The outer flex
          has `gap-2` AND `justify-between`, which means: when there's extra
          space, justify-between pins the left group to the left and the view
          toggle to the right; when the groups meet at narrow widths, gap-2
          guarantees they keep an 8px gap (same as the gap between sibling
          buttons within each group). No flex-wrap — items stay on one row at
          any viewport width. */}
      <div
        className={classNames(
          `sticky top-0 z-20 flex h-10 items-center justify-between gap-2 bg-surface px-10`,
          TOOLBAR_CONTAINER,
        )}
      >
        <div className="flex min-w-0 items-center gap-2">
          {/* `value` is the URL's search text, so the empty state's Clear resets the box too. */}
          <ControlledSearch
            onValueChange={handleSearchChange}
            value={searchValue}
            className="h-9 min-w-0 flex-1 md:w-62.5 md:flex-none"
          />
          <TableFilters
            columns={filterableColumns}
            defaultJoin={data.joiner as any}
            initialFilters={data.initialFilters}
          />
          <AssignedToMeFilterToggle />
          {canShowCompleted && (
            <Button
              type="button"
              variant="white"
              size="md"
              onClick={toggleShowCompleted}
              className={classNames(`gap-x-2`, showCompleted ? `text-blue-600 hover:text-blue-600` : `text-default`)}
              aria-pressed={showCompleted}
              aria-label="Show completed"
              data-testid="tasks-show-completed"
              data-tour="tasks-show-completed"
            >
              <CheckCircle className={showCompleted ? `text-blue-600` : `text-default`} width={16} height={16} />
              <span className="hidden text-ui-xs md:inline">Show completed</span>
            </Button>
          )}
          {view === `table` && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="white" size="md">
                  <MultiplePages className="text-default" width={16} height={16} />
                  <span className="hidden text-ui-xs md:inline">Group by: {groupByLabel}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuRadioGroup value={groupBy} onValueChange={(next) => setGroupBy(next as GroupBy)}>
                  {GROUP_BY_OPTIONS.map((option) => (
                    <DropdownMenuRadioItem key={option.id} value={option.id}>
                      {option.label}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        {/* Table sits on the left because it is the default and the more common view. */}
        <SegmentedControl
          options={[
            { id: `table`, label: `Table`, icon: <Page className="h-3.5 w-3.5" /> },
            { id: `calendar`, label: `Calendar`, icon: <Calendar className="h-3.5 w-3.5" /> },
          ]}
          value={view}
          onSelect={(next: ViewMode) => setView(next)}
        />
      </div>
      {/* Standard edge-scroll mechanism (see AppShell `EDGE_SCROLL_PAGES`):
          `main` drops its `px-10 pt-8` gutter for `/tasks`, so this region
          fills to the window's right edge and we re-supply a left gutter here.

          - table mode: `overflow-auto pl-10 pr-10` — this div is the scroll
            container that owns BOTH axes (TasksTable delegates its scroll up
            to us via `min-w-[960px]`). Symmetric 40px gutters matching the
            header/toolbar `px-10`, so the table ends where the New Task button
            and view toggle end instead of bleeding to the window's edge. Note
            this uses the full `pr-10` (40px), NOT PageScroll's `pr-[34px]`
            scrollbar-compensated value: that trick assumes a child that always
            reserves a vertical scrollbar, but this container is itself the
            scroller and frequently has none (short lists), so 34px would leave
            the table sticking ~6px past the buttons.
          - calendar mode: `px-10` gutter both sides. TasksCalendar is `h-full`
            and scrolls its own day cells, so we must NOT own the overflow
            here, only bound its height.

          `min-h-0` is critical: as a flex item this div defaults to
          `min-height: auto`, which would let it inflate to fit its content and
          break both the table's vertical scroll and the calendar's day-cell
          scroll. With `min-h-0` it stays constrained to the remaining flex-col
          space, giving a fixed height to scroll against.

          `my-4` (16px each side): symmetric breathing room so the calendar's
          bottom is the same distance from the page bottom as its top is from
          the toolbar above it. */}
      <div
        className={classNames(
          `my-4 flex h-full min-h-0 w-full grow flex-col`,
          view === `calendar` ? `px-10` : `overflow-auto pl-10 pr-10`,
        )}
      >
        {allGroup &&
          (view === `calendar` ? (
            <TasksCalendar
              query={allGroup.query}
              onTotalChange={(total) => onTotalChange(allGroup.groupName, total)}
              onContactClick={onContactClickByGroup[allGroup.groupName]}
              onTaskUpdated={handleTaskCreatedOrUpdated}
              onTaskTraversalItemsChange={onTaskTraversalItemsChangeByGroup[allGroup.groupName]}
              refreshTrigger={refreshTrigger}
              highlightTaskId={highlightTaskId}
            />
          ) : (
            <TasksTable
              query={allGroup.query}
              initialSort={allGroup.initialSort}
              onTotalChange={(total) => onTotalChange(allGroup.groupName, total)}
              onContactClick={onContactClickByGroup[allGroup.groupName]}
              onTaskUpdated={handleTaskCreatedOrUpdated}
              onTaskTraversalItemsChange={onTaskTraversalItemsChangeByGroup[allGroup.groupName]}
              refreshTrigger={refreshTrigger}
              highlightTaskId={highlightTaskId}
              groupBy={groupBy}
              emptyState={tasksEmptyState}
            />
          ))}
      </div>
      {/* Render the drawer as soon as the user intends to open it (not just
          after the contact fetch lands) so its slide-in transition actually
          plays. ContactDrawer handles `contact == null` gracefully by showing
          a Spinner in place of the contact body, and the inner DrawerV2
          uses a Headless UI Transition with `show={openDrawer}`. Mounting
          early + flipping `openDrawer` from false→true triggers the
          translate-x-full → translate-x-0 animation. */}
      {openContactDrawer && (
        <ContactDrawer
          contact={focusedContact}
          openDrawer={openContactDrawer}
          onClose={handleContactDrawerClose}
          onChange={handleContactChange}
          onTaskUpdated={handleTaskCreatedOrUpdated}
          nextContact={drawerTaskTraversal.next}
          previousContact={drawerTaskTraversal.prev}
          isNavigating={drawerTaskTraversal.isNavigating}
          onActivityAdded={async (activity) => {
            if (activity.type === `Task`) await handleTaskCreatedOrUpdated()
          }}
        />
      )}
    </div>
  )
})
